import React from "react";

export const PLAN_FEATURE_CATALOG = [
  { key: "users", name: "Users included", kind: "value" },
  { key: "credits", name: "Credits", kind: "value" },
  { key: "shipment_history", name: "Shipment History", kind: "value", unit: "months" },
  { key: "online_training", name: "Online Training", kind: "value", unit: "sessions" },
  { key: "dashboard", name: "Dashboard Access & Reports", kind: "toggle" },
  { key: "email_support", name: "Email Support", kind: "toggle" },
  { key: "performance_report", name: "Performance Report", kind: "toggle" },
  { key: "customs_integration", name: "Customs Integration", kind: "toggle" },
  { key: "white_label", name: "White Label", kind: "toggle" },
  { key: "account_manager", name: "Dedicated Account Manager", kind: "toggle" },
  { key: "sla", name: "SLA Guarantee", kind: "toggle" },
];

const isOn = (value) => String(value || "").toLowerCase() === "true" || value === true;

export const featureLine = (row, cycle) => {
  if (!row || row.enabled === false) return "";
  const yearly = cycle === "yearly";
  const amount = row.kind === "toggle"
    ? (yearly ? row.yearly_value : row.monthly_value)
    : (yearly ? (row.yearly_value || row.monthly_value) : row.monthly_value);
  if (row.kind === "toggle") {
    return isOn(amount) ? row.name : "";
  }
  const text = String(amount || "").trim();
  if (!text) return "";
  if (row.key === "users") return text === "-1" ? "Unlimited users" : `${text} Users included`;
  if (row.key === "credits") return `${text} Credits`;
  return row.unit ? `${text} ${row.unit} ${row.name}` : `${text} ${row.name}`;
};

const norm = (value) => String(value || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();

const catalogKeyFromText = (value) => {
  const text = norm(value);
  if (!text) return "";
  if (text.includes("shipment history")) return "shipment_history";
  if (text.includes("online training") || text.includes("training")) return "online_training";
  if (text.includes("email support")) return "email_support";
  if (text.includes("dashboard")) return "dashboard";
  if (text.includes("performance report")) return "performance_report";
  if (text.includes("customs")) return "customs_integration";
  if (text.includes("white label")) return "white_label";
  if (text.includes("account manager")) return "account_manager";
  if (text === "sla" || text.includes("sla")) return "sla";
  if (text.includes("credit")) return "credits";
  if (text.includes("user")) return "users";
  return "";
};

const lineText = (entry) => {
  if (entry == null) return "";
  if (typeof entry === "string") return entry;
  const name = entry.name || entry.label || "";
  const value = entry.monthly_value || entry.value || "";
  if (value && name && !String(name).toLowerCase().includes(String(value).toLowerCase())) return `${value} ${name}`;
  return String(name || value || "");
};

const parseLine = (entry) => {
  const raw = lineText(entry).trim();
  if (!raw) return null;
  if (norm(raw) === "unlimited users" || norm(raw) === "unlimited users included") return { key: "users", value: "-1" };
  const match = raw.match(/^(-?\d+(?:\.\d+)?)\s+(.+)$/);
  const amount = match ? match[1] : "";
  const label = match ? match[2] : raw;
  const key = catalogKeyFromText(label) || catalogKeyFromText(raw);
  if (!key) return null;
  const item = PLAN_FEATURE_CATALOG.find((entryItem) => entryItem.key === key);
  if (item.kind === "toggle") return { key, value: "true" };
  return { key, value: amount };
};

const textValue = (value) => (value == null || value === "" ? "" : String(value));

export const seedFeatureRows = (data = {}) => {
  const rows = Array.isArray(data.feature_rows) ? data.feature_rows : [];
  const monthlyLines = (Array.isArray(data.features) ? data.features : []).map(parseLine).filter(Boolean);
  const yearlyLines = (Array.isArray(data.yearly_features) ? data.yearly_features : []).map(parseLine).filter(Boolean);

  return PLAN_FEATURE_CATALOG.map((item) => {
    const saved = rows.find((row) => (row.key && row.key === item.key) || catalogKeyFromText(row.name) === item.key) || null;
    const monthlyLine = monthlyLines.find((line) => line.key === item.key);
    const yearlyLine = yearlyLines.find((line) => line.key === item.key);
    let monthly = "";
    let yearly = "";

    if (saved) {
      monthly = textValue(saved.monthly_value !== undefined && saved.monthly_value !== "" ? saved.monthly_value : saved.value);
      yearly = textValue(saved.yearly_value);
      if (!monthly) {
        const parsedName = parseLine(saved.name);
        if (parsedName && parsedName.key === item.key) monthly = parsedName.value;
      }
    }
    if (!monthly && monthlyLine) monthly = monthlyLine.value;
    if (!yearly && yearlyLine) yearly = yearlyLine.value;

    if (!saved && !monthly && !yearly && item.key === "users" && data.max_users != null && data.max_users !== "") {
      monthly = String(data.max_users);
      yearly = data.yearly_max_users != null && data.yearly_max_users !== "" ? String(data.yearly_max_users) : "";
    }
    if (!saved && !monthly && !yearly && item.key === "credits" && data.credits != null && data.credits !== "") {
      monthly = String(data.credits);
      yearly = data.yearly_credits != null && data.yearly_credits !== "" ? String(data.yearly_credits) : "";
    }

    if (item.kind === "toggle") {
      if (monthly && monthly !== "false") monthly = "true";
      if (yearly && yearly !== "false") yearly = "true";
    }

    const hasValue = monthly !== "" || yearly !== "";
    const blankPlan = rows.length === 0 && monthlyLines.length === 0 && yearlyLines.length === 0 && data.max_users == null && data.credits == null;
    const explicitlyOff = Boolean(saved && saved.key && saved.enabled === false);
    const enabled = blankPlan && (item.key === "users" || item.key === "credits")
      ? true
      : explicitlyOff
        ? false
        : (hasValue || saved?.enabled === true || Boolean(monthlyLine || yearlyLine));

    return { ...item, enabled, monthly_value: monthly, yearly_value: yearly };
  });
};

const numberOrEmpty = (value) => {
  if (value === "" || value == null) return "";
  const number = Number(value);
  return Number.isFinite(number) ? number : "";
};

export const packFeatureRows = (rows = [], legacy = {}) => {
  const feature_rows = PLAN_FEATURE_CATALOG.map((item) => {
    const row = (rows || []).find((entry) => entry.key === item.key) || {};
    return {
      key: item.key,
      name: item.name,
      kind: item.kind,
      unit: item.unit || "",
      enabled: row.enabled === true,
      monthly_value: String(row.monthly_value || "").trim(),
      yearly_value: String(row.yearly_value || "").trim(),
    };
  });
  const users = feature_rows.find((row) => row.key === "users");
  const credits = feature_rows.find((row) => row.key === "credits");
  const monthly = feature_rows.map((row) => featureLine(row, "monthly")).filter(Boolean);
  const yearly = feature_rows.map((row) => featureLine(row, "yearly")).filter(Boolean);
  const extras = (list, generated) => {
    const known = new Set(generated);
    return (Array.isArray(list) ? list : [])
      .map((entry) => lineText(entry).trim())
      .filter((line) => line && !known.has(line) && !parseLine(line));
  };
  return {
    feature_rows,
    features: [...monthly, ...extras(legacy.features, monthly)],
    yearly_features: [...yearly, ...extras(legacy.yearly_features, yearly)],
    credits: credits && credits.enabled && numberOrEmpty(credits.monthly_value) !== "" ? numberOrEmpty(credits.monthly_value) : 0,
    yearly_credits: credits && credits.enabled && numberOrEmpty(credits.yearly_value || credits.monthly_value) !== "" ? numberOrEmpty(credits.yearly_value || credits.monthly_value) : null,
    max_users: users && users.enabled && numberOrEmpty(users.monthly_value) !== "" ? numberOrEmpty(users.monthly_value) : 0,
    yearly_max_users: users && users.enabled && numberOrEmpty(users.yearly_value || users.monthly_value) !== "" ? numberOrEmpty(users.yearly_value || users.monthly_value) : null,
  };
};

export default function FeatureRows({ rows, onChange }) {
  const list = seedFeatureRows({ feature_rows: rows });
  const update = (key, field, value) => {
    onChange(list.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };
  return (
    <div>
      <p className="text-muted small">Every feature is listed here. Turn it on for this plan, then set the monthly and yearly value. A switch is yes or no. Leave a feature off on Basic when only Premium should include it. Credits and users are saved from this list.</p>
      {list.map((row) => (
        <div className="row g-2 align-items-center mb-2" key={row.key}>
          <div className="col-md-4">
            <div className="form-check">
              <input className="form-check-input" type="checkbox" checked={row.enabled === true} id={`feature-${row.key}`} onChange={(e) => update(row.key, "enabled", e.target.checked)} />
              <label className="form-check-label" htmlFor={`feature-${row.key}`}>{row.name}</label>
            </div>
          </div>
          {row.kind === "toggle" ? (
            <>
              <div className="col-md-3">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={isOn(row.monthly_value)} disabled={row.enabled === false} onChange={(e) => update(row.key, "monthly_value", e.target.checked ? "true" : "false")} />
                  <label className="form-check-label">Monthly</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={isOn(row.yearly_value)} disabled={row.enabled === false} onChange={(e) => update(row.key, "yearly_value", e.target.checked ? "true" : "false")} />
                  <label className="form-check-label">Yearly</label>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="col-md-3">
                <input className="form-control" type="number" placeholder={row.unit ? `Monthly ${row.unit}` : "Monthly"} value={row.monthly_value} disabled={row.enabled === false} onChange={(e) => update(row.key, "monthly_value", e.target.value)} />
              </div>
              <div className="col-md-3">
                <input className="form-control" type="number" placeholder={row.unit ? `Yearly ${row.unit}` : "Yearly"} value={row.yearly_value} disabled={row.enabled === false} onChange={(e) => update(row.key, "yearly_value", e.target.value)} />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
